import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Card } from 'modules/Statistics/Pulse/components/Card'
import { AddCard } from 'modules/Statistics/Pulse/components/AddCard'
import isEmpty from 'lodash/isEmpty'
import { Grid } from 'gu-ui-kit'

export const Cards = () => {
  const {
    include_vat,
    metrics = [],
    analytics: {
      loading,
      data: { summary = null, items = [] },
    },
  } = useSelector((state) => state['statistics/pulse'])

  const { t } = useTranslation(['pulse'])

  const filteredData = useMemo(() => {
    if (
      !metrics.length ||
      !summary ||
      (summary && !Object.keys(summary).length)
    ) {
      return null
    }

    return metrics
      .filter((metric) => !metric.disabled)
      .map(({ id, isActive }) => {
        const hasChildren = id.indexOf('.') > -1
        let currentSummary = {}

        if (hasChildren) {
          const [key, value] = id.split('.')

          currentSummary = {
            ...summary?.[key]?.children[value],
            id,
            isActive,
            items: items.map((item) => item?.[key]?.[value] ?? 0),
          }
        } else {
          currentSummary = {
            ...summary[id],
            id,
            isActive,
            items: items.map((item) => item?.[id] ?? 0),
          }
        }
        return currentSummary
      })
      .filter((item) => item.value !== null)
  }, [metrics, summary])

  const filteredMetrics = useMemo(
    () => metrics.filter((metric) => !metric.disabled),
    [metrics],
  )

  const unselectedMetrics = useMemo(() => {
    const filteredSummary = {}

    if (!summary) {
      return filteredSummary
    }

    Object.entries(summary).forEach(([key, value]) => {
      if (!isEmpty(value.children)) {
        Object.entries(value.children).forEach(([k, v]) => {
          if (!filteredMetrics.find((m) => m.id === `${key}.${k}`)) {
            if (!filteredSummary[key]) {
              filteredSummary[key] = { children: {} }
            }
            filteredSummary[key].children[k] = v
          }
        })
      } else {
        if (!filteredMetrics.find((m) => m.id === key)) {
          filteredSummary[key] = value
        }
      }
    })

    return filteredSummary
  }, [filteredMetrics, summary])

  if (
    !filteredData ||
    (!loading && (!summary || (summary && isEmpty(summary))))
  ) {
    return null
  }

  return (
    <Grid gap={16} fluid wrap={true} direction={'row'}>
      {filteredData.map(
        ({ id, isActive, value, valueIncludeVat = null, items = [] }) => (
          <Card
            key={id}
            id={id}
            title={
              t(`chart.legend.labels.${id}`) +
              (include_vat && valueIncludeVat
                ? ' ' + t(`chart.legend.labels.include_vat`)
                : '')
            }
            tooltip={t(`metrics.tooltips.${id}`)}
            value={include_vat && valueIncludeVat ? valueIncludeVat : value}
            isActive={isActive}
            include_vat={include_vat}
            data={items.map(({ value, include_vat = null }) => {
              return include_vat && valueIncludeVat ? valueIncludeVat : value
            })}
          />
        ),
      )}

      <AddCard metrics={unselectedMetrics} />
    </Grid>
  )
}

Cards.defaultProps = {
  className: '',
}

Cards.propTypes = {
  className: PropTypes.string,
}
